import { Appearance } from "common/generator/components/button-appearance";
import { ButtonBarBackground, WireDynamicButtonBar } from "common/wire";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import tw from "twin.macro";
import { styled } from "webapp/styled-components";
import classNames from "classnames";
import { SectionStyle, SectionStyleContext, SectionStyleProvider } from "webapp/ui/views/wire-container/wire-container";
import chroma from "chroma-js";
import { isEmptyOrUndefinedish } from "common/Support";
import React from "react";

const ButtonBarStyle = styled.div`
    ${tw`relative flex flex-col items-center p-8 text-center rounded-xl shadow-md-dark text-text-dark`}

    &.none {
        ${tw`p-0 shadow-none`};
    }

    &.soft {
        ${tw`shadow-none`};
        background-color: ${p => chroma(p.theme.accent).alpha(0.1).css()};
    }

    &.card {
        ${tw`bg-bg-front`};
    }

    &.accent,
    &.accent h3,
    &.section-accent.none,
    &.section-accent.none h3 {
        ${tw`text-white`}
    }

    &.start {
        ${tw`items-start text-left`};

        .button-box {
            justify-content: flex-start;
        }
    }

    &.end {
        ${tw`items-end text-right`};

        .button-box {
            justify-content: flex-end;
        }
    }

    &.stretch {
        ${tw`items-stretch text-left`};

        .button-box {
            justify-content: stretch;
        }
    }

    &.wide {
        button {
            flex-grow: 1;
            flex-basis: 0;
            max-width: 50%;
        }
    }
`;

function toSectionStyle(bg: ButtonBarBackground): SectionStyle {
    switch (bg) {
        case ButtonBarBackground.Accent:
            return SectionStyle.Accent;
        case ButtonBarBackground.Soft:
            return SectionStyle.White;
        case ButtonBarBackground.Card:
            return SectionStyle.White;
        case ButtonBarBackground.None:
            return SectionStyle.None;
    }
}

export const ButtonBar: React.VFC<Omit<WireDynamicButtonBar, "kind">> = p => {
    const toRender = p.buttons.filter(b => b.action !== undefined && !isEmptyOrUndefinedish(b.title));

    const newStyle = toSectionStyle(p.background);

    return (
        <SectionStyleProvider value={newStyle}>
            <SectionStyleContext.Consumer>
                {s => (
                    <ButtonBarStyle
                        className={classNames(
                            p.alignment,
                            p.size,
                            p.background,
                            `section-${s}`,
                            p.background === ButtonBarBackground.Accent && "accent-bg"
                        )}>
                        {p.title && <h2 tw="text-2xl font-extrabold mb-2">{p.title}</h2>}
                        {p.description && <h3 tw="mb-6 text-base text-text-pale">{p.description}</h3>}
                        <div tw="flex self-stretch justify-center flex-wrap row-gap[12px]" className="button-box">
                            {toRender.map((b, i) => (
                                <WireButton
                                    tw="not-last:mr-3"
                                    appearance={i === 0 ? Appearance.Filled : Appearance.Bordered}>
                                    {b.title}
                                </WireButton>
                            ))}
                        </div>
                    </ButtonBarStyle>
                )}
            </SectionStyleContext.Consumer>
        </SectionStyleProvider>
    );
};
